<script setup>
import { defineProps, defineEmits, computed, onBeforeUnmount, watch, ref, onMounted, nextTick } from 'vue';

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: 'Confirmação'
  },
  message: {
    type: String,
    required: true
  },
  confirmText: {
    type: String,
    default: 'Confirmar'
  },
  cancelText: {
    type: String,
    default: 'Cancelar'
  },
  confirmColor: {
    type: String,
    default: 'primary'
  },
  loading: {
    type: Boolean,
    default: false
  },
  // Performance optimization props
  eager: {
    type: Boolean, 
    default: true, // Pre-render the modal content for faster display
  },
  noTransition: {
    type: Boolean,
    default: false, // Skip transition animations for faster display
  }
});

const emit = defineEmits(['confirm', 'cancel', 'update:open']);

// Internal state to manage dialog open state
const internalOpen = ref(props.open);
const dialogRef = ref(null);
const cardRef = ref(null); // Add a ref for the card element

// Use watchEffect instead of watch for better performance
watch(() => props.open, (newValue) => {
  if (newValue !== internalOpen.value) {
    internalOpen.value = newValue;
  }
}, { flush: 'sync' }); // Use sync to apply changes immediately

// Create a computed property for dialog state that emits the update:open event
const dialogOpen = computed({
  get: () => internalOpen.value,
  set: (value) => {
    if (internalOpen.value !== value) {
      internalOpen.value = value;
      emit('update:open', value);
    }
  }
});

const handleConfirm = () => {
  try {
    emit('confirm');
  } catch (error) {
    console.error('Error in confirmation handler:', error);
  }
};

const handleCancel = () => {
  try {
    emit('cancel');
  } catch (error) {
    console.error('Error in cancel handler:', error);
  }
};

// For better performance, use the onMounted hook to optimize initial render
onMounted(() => {
  // For extreme performance needs, we can pre-cache the dialog in its open state
  // and then hide it initially, which makes subsequent opens faster
  if (props.eager && !props.open) {
    // Pre-render the dialog content
    nextTick(() => {
      // Fix: Use cardRef instead since it's more reliable
      // and add proper error handling
      try {
        if (cardRef.value) {
          // Force a render cycle to ensure the DOM is ready
          const element = cardRef.value.$el || cardRef.value;
          if (element && typeof element.getBoundingClientRect === 'function') {
            element.getBoundingClientRect();
          }
        }
      } catch (error) {
        console.warn('Error during dialog pre-rendering:', error);
        // Silent fail - this is just an optimization
      }
    });
  }
});

// Safety: ensure modal is closed when component is unmounted
onBeforeUnmount(() => {
  if (internalOpen.value) {
    emit('update:open', false);
  }
});
</script>

<template>
  <v-dialog 
    ref="dialogRef"
    v-model="dialogOpen" 
    max-width="400" 
    persistent
    theme="light"
    :retain-focus="false"
    :scrim="false"
    :eager="eager"
    :transition="noTransition ? false : 'dialog-transition'"
    content-class="confirmation-dialog"
  >
    <v-card ref="cardRef" class="confirmation-card">
      <v-card-title class="text-h5" :class="`bg-${confirmColor} text-white`">
        <v-icon start icon="mdi-help-circle" class="mr-2"></v-icon>
        {{ title }}
      </v-card-title>
      
      <v-card-text class="pt-4 pb-0">
        <p class="text-body-1 mb-4">{{ message }}</p>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn 
          color="grey-darken-1" 
          variant="text" 
          @click="handleCancel"
          :disabled="loading"
        >
          {{ cancelText }}
        </v-btn>
        <v-btn 
          :color="confirmColor" 
          @click="handleConfirm"
          :loading="loading"
          :disabled="loading"
        >
          {{ confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.confirmation-dialog {
  will-change: transform, opacity;
}

.confirmation-card {
  will-change: transform;
  border: solid 1px rgba(0, 0, 0, 0.2) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important; /* Better-looking but GPU-accelerated shadow */
  transform: translateZ(0); /* Force GPU acceleration */
}

:deep(.v-overlay__scrim) {
  background-color: transparent !important;
}

:deep(.v-overlay__content) {
  box-shadow: none !important;
}

</style>
